/**
 * Общие части четырёх представлений задач (docs/07-tasks-notion.md).
 * Ни на одном виде нет создания и редактирования: правки идут в Notion.
 */
import { Badge, Icon } from '@/ds';
import type { TaskAssignee, TaskRow, TaskStatus } from '@/api/hooks';
import { formatDateShort } from '@/lib/format';

export const STATUS_LABELS: Record<TaskStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
};

export const STATUS_ORDER: TaskStatus[] = ['not_started', 'in_progress', 'done'];

/** Сегодня по Москве: сроки читаются в рабочем часовом поясе. */
export function todayIso(now: Date = new Date()): string {
  return new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function isOverdue(task: TaskRow, today = todayIso()): boolean {
  if (task.status === 'done') return false;
  const end = task.dueDateEnd ?? task.dueDate;
  return end !== null && end < today;
}

/** Срок с пометкой просрочки. Тревога передаётся цветом текста, не заливкой. */
export function DueDate({ task }: { task: TaskRow }) {
  if (!task.dueDate) {
    return <span style={{ color: 'var(--text-subtle)' }}>без срока</span>;
  }

  const overdue = isOverdue(task);
  const label = task.dueDateEnd
    ? `${formatDateShort(task.dueDate)} — ${formatDateShort(task.dueDateEnd)}`
    : formatDateShort(task.dueDate);

  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-xs)',
        color: overdue ? 'var(--status-warning-fg)' : 'var(--text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

export function StatusBadge({ status }: { status: TaskStatus }) {
  const tone = status === 'done' ? 'success' : status === 'in_progress' ? 'brand' : 'neutral';
  return (
    <Badge tone={tone} dot={status !== 'not_started'}>
      {STATUS_LABELS[status]}
    </Badge>
  );
}

export function Assignees({ people }: { people: TaskAssignee[] }) {
  if (people.length === 0) {
    return <span style={{ color: 'var(--text-subtle)' }}>не назначено</span>;
  }
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {people.map((person) => (
        <span
          key={person.notionUserId}
          title={person.userId ? undefined : 'Участник не сопоставлен с пользователем приложения'}
          style={{
            font: '400 13px var(--font-text)',
            color: person.userId ? 'var(--text-body)' : 'var(--text-subtle)',
            whiteSpace: 'nowrap',
          }}
        >
          {person.name}
        </span>
      ))}
    </span>
  );
}

/** Единственный способ что-либо изменить — перейти в Notion. */
export function NotionLink({ task, compact }: { task: TaskRow; compact?: boolean }) {
  return (
    <a
      href={task.notionUrl}
      target="_blank"
      rel="noreferrer"
      className="kj-link-plain"
      title="Открыть в Notion"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        font: '400 13px var(--font-text)',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon name="ExternalLink" size={14} />
      {compact ? null : 'В Notion'}
    </a>
  );
}

export function ProjectTag({ name }: { name: string | null }) {
  if (!name) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px var(--space-3)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-brand-soft)',
        color: 'var(--burgundy-800)',
        font: '400 12px var(--font-text)',
        whiteSpace: 'nowrap',
      }}
    >
      {name}
    </span>
  );
}

/** Сортировка по сроку: задачи без срока всегда в конце. */
export function byDueDate(a: TaskRow, b: TaskRow): number {
  if (a.dueDate === null && b.dueDate === null) return a.title.localeCompare(b.title, 'ru');
  if (a.dueDate === null) return 1;
  if (b.dueDate === null) return -1;
  return a.dueDate.localeCompare(b.dueDate);
}
